/**
 * useCardGridNavigation hook
 *
 * Manages keyboard navigation (Arrow keys) between moderation case cards
 * using a roving tabindex pattern. Moves DOM focus to the newly focused card.
 */

import { useCallback, useRef } from 'react';

import type { ModerationCase } from '../domain/ModerationCase';
import { scrollCardIntoView } from '../utils/scrollCardIntoView';

interface UseCardGridNavigationParams {
  /** All session cases currently displayed */
  sessionCases: ModerationCase[];
  /** Currently focused case ID */
  focusedCaseId: string | null;
  /** Sets focus on a specific case card */
  focusCase: (caseId: string) => void;
}

export interface UseCardGridNavigationReturn {
  /** Keyboard event handler for arrow navigation */
  handleCardGridKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  /** Ref to attach to the card grid container */
  cardGridRef: React.RefObject<HTMLDivElement | null>;
}

/** Computes the next index for arrow-key navigation with wrapping. */
function computeNextIndex(key: string, currentIndex: number, length: number): number | null {
  if (key === 'ArrowRight' || key === 'ArrowDown') {
    return currentIndex < length - 1 ? currentIndex + 1 : 0;
  }

  if (key === 'ArrowLeft' || key === 'ArrowUp') {
    return currentIndex > 0 ? currentIndex - 1 : length - 1;
  }

  return null;
}

/**
 * Hook that provides keyboard navigation between moderation case cards.
 * Supports ArrowRight/Down (next) and ArrowLeft/Up (previous) with wrapping.
 *
 * @returns Keyboard handler and grid ref
 */
export function useCardGridNavigation({ sessionCases, focusedCaseId, focusCase }: UseCardGridNavigationParams): UseCardGridNavigationReturn {
  const cardGridRef = useRef<HTMLDivElement>(null);

  const handleCardGridKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (sessionCases.length === 0) return;

      const currentIndex = sessionCases.findIndex(c => c.caseId === focusedCaseId);
      const nextIndex = computeNextIndex(event.key, currentIndex, sessionCases.length);

      if (nextIndex === null) return;

      event.preventDefault();
      focusCase(sessionCases[nextIndex].caseId);

      // Move DOM focus to the newly focused card
      const cardItems = cardGridRef.current?.querySelectorAll<HTMLElement>('[role="option"]');

      cardItems?.[nextIndex]?.focus({ preventScroll: true });

      // Scroll the card grid container if the target card is outside the visible area
      const scrollContainer = cardGridRef.current?.parentElement;
      const targetCard = cardItems?.[nextIndex];

      if (scrollContainer && targetCard) {
        scrollCardIntoView(scrollContainer, targetCard);
      }
    },
    [sessionCases, focusedCaseId, focusCase]
  );

  return {
    handleCardGridKeyDown,
    cardGridRef,
  };
}
